import React from "react";
import { Plus, Minus, GripVertical, X } from "lucide-react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	arrayMove,
} from "@dnd-kit/sortable";
import { SortableItem, DragHandle } from "../../ui/sortable-item";
import { BaseFieldPreviewProps } from "./previewUtils";
import { Button } from "../../../../../components/ui/button";
import { Input } from "../../../../../components/ui/input";
import { FormField, FieldType } from "../../../types/formTypes";
import { createNewAppointmentField, handleFieldTypeChange } from "../../../utils/fieldUtils";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../../components/ui/select";
import { Switch } from "../../../../../components/ui/switch";
import { Label } from "../../../../../components/ui/label";
import { Calendar, Clock, Type, List, AlignLeft, CheckSquare, Circle, FileUp } from "lucide-react";

const appointmentFieldTypeConfig: Array<{
	type: FieldType;
	label: string;
	icon: React.ReactNode;
}> = [
	{ type: 'text', label: 'Kurzantwort', icon: <Type className="h-4 w-4" /> },
	{ type: 'textarea', label: 'Lange Antwort', icon: <AlignLeft className="h-4 w-4" /> },
	{ type: 'checkbox', label: 'Mehrfachauswahl', icon: <CheckSquare className="h-4 w-4" /> },
	{ type: 'radio', label: 'Einzelauswahl', icon: <Circle className="h-4 w-4" /> },
	{ type: 'select', label: 'Dropdown', icon: <List className="h-4 w-4" /> },
	{ type: 'file', label: 'Datei hochladen', icon: <FileUp className="h-4 w-4" /> },
	{ type: 'date', label: 'Datum', icon: <Calendar className="h-4 w-4" /> },
	{ type: 'time', label: 'Zeit', icon: <Clock className="h-4 w-4" /> },
];

const AppointmentFieldPreview: React.FC<BaseFieldPreviewProps> = ({
	field,
	renderDescription,
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
				tolerance: 5,
				delay: 150,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const dispatchUpdate = (updatedFields: FormField[], maxAppointments?: number, clearMax: boolean = false) => {
		const detail: any = {
			fieldId: field.id,
			appointmentFields: updatedFields,
		};

		if (clearMax || maxAppointments !== undefined) {
			detail.maxAppointments = maxAppointments;
		}

		const customEvent = new CustomEvent("appointmentFieldsUpdate", {
			detail,
			bubbles: true,
		});
		document.dispatchEvent(customEvent);
		field.appointmentFields = updatedFields;

		if (clearMax) {
			field.maxAppointments = undefined;
		} else if (maxAppointments !== undefined) {
			field.maxAppointments = maxAppointments;
		}
	};

	const addAppointmentField = () => {
		const currentFields = field.appointmentFields || [];
		const newAppointmentField = createNewAppointmentField("text");
		const updatedAppointmentFields = [...currentFields, newAppointmentField];
		dispatchUpdate(updatedAppointmentFields);
	};

	const removeAppointmentField = (index: number) => {
		if (!field.appointmentFields || field.appointmentFields.length === 0) return;
		const updatedAppointmentFields = [...field.appointmentFields];
		updatedAppointmentFields.splice(index, 1);
		dispatchUpdate(updatedAppointmentFields);
	};

	const updateAppointmentFieldLabel = (index: number, newLabel: string) => {
		if (!field.appointmentFields) return;
		const updatedAppointmentFields = [...field.appointmentFields];
		updatedAppointmentFields[index] = {
			...updatedAppointmentFields[index],
			label: newLabel,
		};
		dispatchUpdate(updatedAppointmentFields);
	};

	const updateAppointmentFieldType = (index: number, newType: FieldType) => {
		if (!field.appointmentFields) return;
		const currentField = field.appointmentFields[index];
		const updatedField = handleFieldTypeChange(currentField, newType);

		const updatedAppointmentFields = [...field.appointmentFields];
		updatedAppointmentFields[index] = updatedField;
		dispatchUpdate(updatedAppointmentFields);
	};

	const addOptionToAppointmentField = (fieldIndex: number) => {
		if (!field.appointmentFields) return;
		const appointmentField = field.appointmentFields[fieldIndex];
		if (!appointmentField.options) return;

		const updatedAppointmentFields = [...field.appointmentFields];
		updatedAppointmentFields[fieldIndex] = {
			...appointmentField,
			options: [...appointmentField.options, `Option ${appointmentField.options.length + 1}`],
		};
		dispatchUpdate(updatedAppointmentFields);
	};

	const removeOptionFromAppointmentField = (fieldIndex: number, optionIndex: number) => {
		if (!field.appointmentFields) return;
		const appointmentField = field.appointmentFields[fieldIndex];
		if (!appointmentField.options || appointmentField.options.length <= 2) return;

		const updatedAppointmentFields = [...field.appointmentFields];
		const updatedOptions = [...appointmentField.options];
		updatedOptions.splice(optionIndex, 1);

		updatedAppointmentFields[fieldIndex] = {
			...appointmentField,
			options: updatedOptions,
		};
		dispatchUpdate(updatedAppointmentFields);
	};

	const updateAppointmentFieldOption = (
		fieldIndex: number,
		optionIndex: number,
		newValue: string
	) => {
		if (!field.appointmentFields) return;
		const appointmentField = field.appointmentFields[fieldIndex];
		if (!appointmentField.options) return;

		const updatedAppointmentFields = [...field.appointmentFields];
		const updatedOptions = [...appointmentField.options];
		updatedOptions[optionIndex] = newValue;

		updatedAppointmentFields[fieldIndex] = {
			...appointmentField,
			options: updatedOptions,
		};
		dispatchUpdate(updatedAppointmentFields);
	};

	const updateAppointmentFieldPlaceholder = (index: number, newPlaceholder: string) => {
		if (!field.appointmentFields) return;
		const updatedAppointmentFields = [...field.appointmentFields];
		updatedAppointmentFields[index] = {
			...updatedAppointmentFields[index],
			placeholder: newPlaceholder,
		};
		dispatchUpdate(updatedAppointmentFields);
	};

	const updateAppointmentFieldRequired = (index: number, required: boolean) => {
		if (!field.appointmentFields) return;
		const updatedAppointmentFields = [...field.appointmentFields];
		updatedAppointmentFields[index] = {
			...updatedAppointmentFields[index],
			required,
		};
		dispatchUpdate(updatedAppointmentFields);
	};

	const updateMaxAppointments = (value: string) => {
		if (value === "" || value === "0") {
			dispatchUpdate(field.appointmentFields || [], undefined, true);
		} else {
			const numValue = parseInt(value, 10);
			dispatchUpdate(field.appointmentFields || [], numValue, false);
		}
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (!field.appointmentFields || !over || active.id === over.id) return;

		const activeIndex = field.appointmentFields.findIndex((f: FormField) => f.id === active.id);
		const overIndex = field.appointmentFields.findIndex((f: FormField) => f.id === over.id);

		if (activeIndex !== -1 && overIndex !== -1) {
			const reorderedFields = arrayMove(field.appointmentFields, activeIndex, overIndex) as FormField[];
			dispatchUpdate(reorderedFields);
		}
	};

	return (
		<div className="space-y-4">
			{renderDescription()}

			<div className="bg-gray-50 p-4 rounded-md space-y-4">
				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<span className="text-sm font-medium">Felder pro Termin</span>
					</div>

					{field.appointmentFields && field.appointmentFields.length > 0 ? (
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
						>
							<SortableContext
								items={field.appointmentFields.map((f: FormField) => f.id)}
								strategy={verticalListSortingStrategy}
							>
								<div className="space-y-3">
									{field.appointmentFields.map((subField: FormField, index: number) => {
										const currentConfig = appointmentFieldTypeConfig.find(c => c.type === subField.type);

										return (
											<SortableItem key={subField.id} id={subField.id} handleSelector=".drag-handle">
												<div className="bg-white p-3 rounded-md border space-y-2">
													<div className="flex items-center gap-2">
														<DragHandle>
															<div className="drag-handle cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded">
																<GripVertical className="h-4 w-4 text-gray-400" />
															</div>
														</DragHandle>
														<Input
															value={subField.label}
															onChange={(e) =>
																updateAppointmentFieldLabel(index, e.target.value)
															}
															placeholder="Feldbezeichnung"
															className="h-8 flex-1"
															aria-label={`Bezeichnung für Feld ${index + 1}`}
															data-testid={`appointment-field-label-${index}`}
														/>
														<Select
															value={subField.type}
															onValueChange={(val) => updateAppointmentFieldType(index, val as FieldType)}
														>
															<SelectTrigger className="w-[150px] h-8" aria-label="Feldtyp auswählen">
																<SelectValue>
																	{currentConfig && (
																		<div className="flex items-center gap-2">
																			{currentConfig.icon}
																			<span className="text-sm">{currentConfig.label}</span>
																		</div>
																	)}
																</SelectValue>
															</SelectTrigger>
															<SelectContent>
																{appointmentFieldTypeConfig.map(({ type, label, icon }) => (
																	<SelectItem key={type} value={type}>
																		<div className="flex items-center gap-2">
																			{icon}
																			<span>{label}</span>
																		</div>
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
														<Button
															type="button"
															variant="ghost"
															size="sm"
															onClick={() => removeAppointmentField(index)}
															className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
															aria-label={`Feld ${index + 1} entfernen`}
															data-testid={`remove-appointment-field-${index}`}
														>
															<Minus className="h-4 w-4" />
														</Button>
													</div>

													{(subField.type === "select" || subField.type === "radio" || subField.type === "checkbox") && subField.options && (
														<div className="space-y-2 pl-4">
															{subField.options.map((option: string, optionIndex: number) => (
																<div
																	key={optionIndex}
																	className="flex items-center gap-2"
																>
																	<div className="text-xs text-gray-500 w-4">
																		{optionIndex + 1}.
																	</div>
																	<Input
																		value={option}
																		onChange={(e) =>
																			updateAppointmentFieldOption(
																				index,
																				optionIndex,
																				e.target.value
																			)
																		}
																		placeholder={`Option ${optionIndex + 1}`}
																		className="h-8 flex-1"
																		aria-label={`Option ${optionIndex + 1} für Feld ${index + 1}`}
																		data-testid={`appointment-field-${index}-option-${optionIndex}`}
																	/>
																	<Button
																		type="button"
																		variant="ghost"
																		size="sm"
																		onClick={() =>
																			removeOptionFromAppointmentField(
																				index,
																				optionIndex
																			)
																		}
																		disabled={subField.options!.length <= 2}
																		className="h-8 w-8 p-0"
																		aria-label={`Option ${optionIndex + 1} entfernen`}
																		data-testid={`remove-appointment-field-${index}-option-${optionIndex}`}
																	>
																		<Minus className="h-4 w-4" />
																	</Button>
																</div>
															))}
															<div className="flex items-center gap-2">
																<div className="text-xs text-gray-500 w-4">
																	{subField.options.length + 1}.
																</div>
																<Button
																	type="button"
																	variant="ghost"
																	size="sm"
																	onClick={() => addOptionToAppointmentField(index)}
																	className="w-full flex items-center justify-start text-gray-500 h-8 text-sm"
																	aria-label="Option hinzufügen"
																	data-testid={`add-appointment-field-${index}-option`}
																>
																	<Plus className="h-3 w-3 mr-1" />
																	Option hinzufügen
																</Button>
															</div>
														</div>
													)}

													{(subField.type === "text" || subField.type === "textarea") && (
														<div className="pl-4">
															<Input
																value={subField.placeholder || ""}
																onChange={(e) =>
																	updateAppointmentFieldPlaceholder(index, e.target.value)
																}
																placeholder="Platzhaltertext"
																className="h-8"
																aria-label={`Platzhalter für Feld ${index + 1}`}
																data-testid={`appointment-field-${index}-placeholder`}
															/>
														</div>
													)}

													{subField.type === "date" && (
														<div className="pl-4 text-xs text-gray-500">
															Datumsauswahl
														</div>
													)}

													{subField.type === "time" && (
														<div className="pl-4 text-xs text-gray-500">
															Zeitauswahl
														</div>
													)}

													{subField.type === "file" && (
														<div className="pl-4 text-xs text-gray-500">
															Datei-Upload
														</div>
													)}

													<div className="pl-4 pt-2 border-t flex items-center gap-2">
														<Switch
															id={`required-${subField.id}`}
															checked={subField.required}
															onCheckedChange={(checked) => updateAppointmentFieldRequired(index, checked)}
															aria-label="Pflichtfeld"
															data-testid={`appointment-field-${index}-required`}
														/>
														<Label htmlFor={`required-${subField.id}`} className="text-sm cursor-pointer">
															Pflichtfeld
														</Label>
													</div>
												</div>
											</SortableItem>
										);
									})}
								</div>
							</SortableContext>
						</DndContext>
					) : (
						<div className="text-sm text-gray-500 text-center py-2">
							Fügen Sie Felder hinzu, die für jeden Termin ausgefüllt werden sollen
						</div>
					)}

					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={addAppointmentField}
						className="w-full flex items-center justify-center"
						aria-label="Feld hinzufügen"
						data-testid="add-appointment-field"
					>
						<Plus className="h-4 w-4 mr-2" />
						Feld hinzufügen
					</Button>
				</div>

				<div className="border-t pt-3">
					<label className="text-sm font-medium block mb-2">
						Maximale Anzahl Termine (optional)
					</label>
					<div className="flex items-center gap-2">
						<Input
							type="number"
							min="0"
							value={field.maxAppointments || ""}
							onChange={(e) => updateMaxAppointments(e.target.value)}
							placeholder="Unbegrenzt"
							className="h-8 w-32"
							aria-label="Maximale Anzahl Termine"
							data-testid="max-appointments-input"
						/>
						{field.maxAppointments && (
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() => updateMaxAppointments("0")}
								className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700"
								aria-label="Maximum entfernen"
								data-testid="clear-max-appointments"
							>
								<X className="h-4 w-4" />
							</Button>
						)}
					</div>
					<p className="text-xs text-gray-500 mt-1">
						0 oder leer lassen für unbegrenzte Anzahl
					</p>
				</div>
			</div>

		</div>
	);
};

export default AppointmentFieldPreview;
